"use client"

import { LazyMotion, domAnimation, m } from 'framer-motion'
import { useTranslations } from 'next-intl';
import React from 'react'
import GhostBtn from '../GhostBtn';
import { Quote } from 'lucide-react';
import ROUTES from '@/lib/routes';
import { useLandingCms } from '@/app/contexts/LandingCmsContext';
import { useLocalizedRoute } from '@/app/lib/useLocalizedRoute';

type ProductOverviewProps = {
    showCaseStudyButton?: boolean;
};

const ProductOverview: React.FunctionComponent<ProductOverviewProps> = ({
    showCaseStudyButton = true,
}) => {
    const t = useTranslations("ProductOverview");
    const cms = useLandingCms();
    const { getLocalizedRoute } = useLocalizedRoute();

    // Helper function to get translation with fallback
    const getTranslation = (key: string, fallback: string) => {
        const translated = t(key);
        if (
            translated === `ProductOverview.${key}` ||
            translated.startsWith("ProductOverview.")
        ) {
            return fallback;
        }
        return translated;
    };

    const title =
        cms?.productOverviewTitle?.trim() ||
        getTranslation("title", "PineGen AI is a Pine Script Code Generator built even for traders with no coding knowledge, helps turn trading ideas into compiler-validated strategies that can be refined, tested, and improved faster.");
    const subtitle =
        cms?.productOverviewSubtitle?.trim() ||
        getTranslation("subtitle", "Team PineGen AI");
    const ctaText =
        cms?.productOverviewCtaText?.trim() ||
        getTranslation("viewCaseStudyBtn", "View Case Study");

    return (
        <LazyMotion features={domAnimation}>
        <section className="relative w-full  flex flex-col items-center justify-center overflow-hidden py-20 bg-[#191919]">
            {/* Main Content Container */}
            <div className="relative z-10 w-full max-w-[990px] mx-auto px-8 lg:px-12 h-full flex flex-col justify-center">
                {/* Title Section */}
                <div className="text-center">
                    <div className="text-center">
                        <m.div
                            initial={{ opacity: 0, y: -20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6 }}
                            className="mb-3 sm:mb-4 md:mb-6 leading-relaxed lg:text-[32px] text-lg sm:text-2xl  text-[#767EF6] relative inline-block"
                            style={{
                                fontStyle: "normal",
                                fontWeight: 700,
                            }}
                        >
                            {/* The Styled Quote Icon */}
                            <Quote
                                fill="currentColor"
                                className="inline lg:-mt-20 sm:-mt-16 -mt-10 -rotate-180 sm:size-6 size-3"
                            />

                            <h2 className="px-2 inline leading-relaxed">
                                {title}
                            </h2>

                        </m.div>
                    </div>

                    <m.p
                        initial={{ opacity: 0, y: -20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 , delay: 0.2}}
                        className='text-base sm:text-lg lg:text-xl text-[#FCFAFA] font-semibold mt-4'
                    >{subtitle}
                    </m.p>

                    {showCaseStudyButton && (
                        <m.div
                            initial={{ opacity: 0, y: -20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6 , delay: 0.4}}
                        >
                            <GhostBtn
                                translationNamespace="ProductOverview"
                                translationKey="viewCaseStudyBtn"
                                fallbackText="View Case Study"
                                labelOverride={ctaText}
                                route={getLocalizedRoute(ROUTES.RESOURCE_CASE_STUDY)}
                                className='mt-12'
                            />
                        </m.div>
                    )}
                </div>
            </div>
        </section>
        </LazyMotion>
    )
}

export default ProductOverview